// #region: Class definition *********************************************************************/
/**
 * @class TabStrip
 * @description Handles the logic for a tabstrip Web Component.
 * 
 * @extends WebComponent
 * Extends the base WebComponent class.
 *
 * @example
 * <tab-strip id="tabstrip" default-tab="tab-id" callback="callback-function">
 *   <ul>
 *     <li>
 *       <input type="radio" name="tabs" id="tab-id" data-page="url-to-load-in-tab-id-page" />
 *       <label for="tab-id">Tab Label</label>
 *     </li>
 *     ...additional tab items
 *   </ul>
 *   <div class="frames">
 *     <iframe id="tab-id-page"></iframe>
 *     ...additional frames matching tab IDs
 *   </div>
 * </tab-strip>
 *
 * @property {string} default-tab - Which tab is activated on render.
 * @property {Function} callback - The function called on tab click.
 *
 * @remarks
 *     The attributes are optional.
*/
class TabStrip extends WebComponent {

    // #region: Private Fields *******************************************************************/
    /**
     * Internal variable for callback function. A callback is called on tab click.
     * @type {Function|null}
     * 
     * @private
     */
    #callback;

    /**
     * Internal variable for default tab.
     * @type {string}
     * 
     * @private
     */
    #defaultTab;
    // #endregion ********************************************************************************/

    // #region: Constructor **********************************************************************/
    /**
     * Creates an instance of TabStrip.
     */
    constructor() {
        super();

        this.#callback = null;
        this.#defaultTab = '';

        this.isSwitchingTab = false;
        this.tabs = [];

        this.tabs = [...this.querySelectorAll('input[name=tabs]')];

        // Add the events to the tabstrip instance.
        this.tabs.forEach((tab) => {
            // Add the click event on the radio (tab) to (re)load the url.
            // We use 'click' as the 'change' event will not fire when we set the selected radio to checked from script.
            // We can however reliably fire the click event.
            tab.addEventListener('click', (e) => {
                if (this.callback && !this.callback(e, tab)) {
                    return false;
                }

                // Select active tab.
                this.handleTabChange(tab.id);

                return true;
            }, { capture: false });
        });
    }
    // #endregion ********************************************************************************/

    // #region: Build ****************************************************************************/
    /**
     * Registered tag name for this web component.
     * This is used by the browser to identify and register the custom element.
     * @type {string}
     * @static
     * @memberof TabStrip
     */
    static tagName = 'tab-strip';

    /**
     * Identifies class instance.
     * @returns {string} The string representation of this class instance.
     * 
     * @memberof TabStrip
     */
    toString() {
        return '[TabStrip]';
    }

    /**
     * Identifies class.
     * @returns {string} The string representation of this class.
     * 
     * @static
     * @memberof TabStrip
     */
    static toString() {
        return '[TabStrip]';
    }

    /**
     * Identifies the version.
     * @returns {string} The version number of this class.
     * 
     * @static
     * @memberof TabStrip
     */
    static version() {
        return '1.0.0';
    }
    // #endregion ********************************************************************************/

    // #region: Static Properties ****************************************************************/
    /**
     * Defines the observed attributes for the custom element.
     * @returns {string[]} Array of attribute names to observe.
     * 
     * @static
     * @memberof TabStrip
     */
    static get observedAttributes() {
        return [...(super.observedAttributes || []), 'callback', 'default-tab'];
    }
    // #endregion ********************************************************************************/

    // #region: Properties ************************************************************************/
    /**
     * Gets the default tab.
     * @returns {string} The default tab id.
     * 
     * @memberof TabStrip
     */
    get defaultTab() {
        return this.#defaultTab;
    }

    /**
     * Sets the default tab.
     * @param {string} value The default tab id to set.
     * 
     * @memberof TabStrip
     */
    set defaultTab(value) {
        this.#defaultTab = value;

        if (this.isConnected) {
            this.selectTab(core.getQueryParameter('tab') || this.#defaultTab);
        }
    }

    /**
     * Gets the callback function for tab click events.
     * @returns {Function|null} The callback function or null if not set.
     * 
     * @memberof TabStrip
     */
    get callback() {
        return this.#callback;
    }

    /**
     * Sets the callback function for tab click events.
     * @param {Function|string|null} value The callback function to set.
     * 
     * @memberof TabStrip
     */
    set callback(value) {
        if (typeof value === 'string') {
            this.#callback = window[value] ?? top.window[value];
        }
        else if (typeof value === 'function') {
            this.#callback = value;
        }
        else {
            this.#callback = null;
        }
    }
    // #endregion ********************************************************************************/

    // #region: Lifecycle events *****************************************************************/
    /**
     * Called when observed attributes change.
     * @param {string} name The name of the attribute that changed.
     * @param {string|null} oldValue The previous value of the attribute.
     * @param {string|null} newValue The new value of the attribute.
     * 
     * @memberof TabStrip
     */
    attributeChangedCallback(name, oldValue, newValue) {
        super.attributeChangedCallback(name, oldValue, newValue);

        if (oldValue === newValue) {
            return;
        }

        switch (name) {

            case 'default-tab':
                this.#defaultTab = newValue;

                break;

            case 'callback':
                this.#callback = newValue;

                break;

            default:
                break;
        
        }
    }

    /**
     * Called when the custom element is attached to the DOM.
     * Extends to setup tab event listeners and initial tab selection.
     * 
     * @memberof TabStrip
     */
    connectedCallback() {
        super.connectedCallback();

        // Listen for URL change.
        top.window.addEventListener('popstate', () => {
            const urlTab = isEmbed ? core.getEmbedUrl().searchParams.get('tab') : core.getQueryParameter('tab');

            this.selectTab(urlTab || this.#defaultTab);
        });

        // Set initial tab.
         
        this.selectTab(core.getQueryParameter('tab', window) || this.#defaultTab);
    }
    // #endregion ********************************************************************************/

    // #region: Public Methods *******************************************************************/
    /**
     * Enables all tabs.
     * 
     * @memberof TabStrip
     */
    enableAllTabs() {
        this.tabs.forEach((tab) => {
            tab.removeAttribute('disabled');
        });
    }

    /**
     * Returns the active tab iframe.
     * @returns {HTMLIFrameElement} The currently active tab iframe, or the first tab.
     * 
     * @memberof TabStrip
     */
    getActiveTab() {
        return this.querySelector('.frames > iframe.active') ?? this.tabs[0];
    }

    /**
     * Performs the action needed to switch tabs.
     * @param {string} newTabId The id for the new tab selected.
     * 
     * @memberof TabStrip
     */
    handleTabChange(newTabId) {
        const activeTab = this.getActiveTab();
        const url = this.querySelector(`#${newTabId}`).dataset.page;
        const iframe = this.querySelector(`#${newTabId}-page`);

        activeTab.classList.remove('active');
        iframe.classList.add('active');

        this.querySelector(`#${newTabId}`).checked = true;

        if (iframe.contentWindow?.location.href.includes(url)) {
            this.switchTabUrl(top.window, newTabId, iframe);

            this.isSwitchingTab = false;
        }
        else {
            const loadHandler = () => {
                this.switchTabUrl(top.window, newTabId, iframe);
                this.isSwitchingTab = false;

                iframe.removeEventListener('load', loadHandler);
            };

            iframe.addEventListener('load', loadHandler);
             
            iframe.src = baseUrl + url;
        }
    }

    /**
     * In some cases the current tab may need to be reloaded.
     * 
     * @memberof TabStrip
     */
    reloadActiveTab() {
        const currentTab = document.querySelector('iframe.active');

        currentTab.addEventListener('load', function handler() {
            this.removeEventListener('load', handler);
        }, { capture: false });

        currentTab.contentWindow.location.reload(true);
    }

    /**
     * Changing/saving any of the content in any of the tabs (might) need a reload to reflect the change.
     * @param {string} tabId The id of the tab to reload.
     * 
     * @memberof TabStrip
     */
    reloadTab(tabId) {
        document.querySelector(`#${tabId}-page`)?.contentWindow.location.reload(true);
    }

    /**
     * Changing/saving any of the content in any of the tabs (might) need a reload to reflect the change.
     * 
     * @memberof TabStrip
     */
    reloadTabs() {
        [...document.querySelectorAll('iframe:not(.active)')].forEach((frame) => {
            if (frame.src) {
                frame.contentWindow.location.reload(true);
            }
        });
    }

    /**
     * Performs the action needed to undo the switching of the tabs, make the original tab button active again.
     * @param {HTMLInputElement} newTab The new tab element selected.
     * 
     * @memberof TabStrip
     */
    resetTabChange(newTab) {
        document.querySelector(`#${this.getActiveTab().id.replace('-page', '')}`).checked = true;
        newTab.checked = false;
    }

    /**
     * Select the tab (by its id).
     * Also called from the outside (on push state change).
     * @param {string} id The tab to show.
     * 
     * @memberof TabStrip
     */
    selectTab(id) {
        if (!id) {
            return;
        }

        const tab = document.querySelector('#' + id);

        if (tab && !tab.checked) {
            this.isSwitchingTab = true;

            tab.disabled = false;
            tab.click();
        }
    }

    /**
     * Update tab info on the document and browsing history.
     * @param {Window} mainWindow The window reference to use.
     * @param {string} id The identifier for the tab to select.
     * @param {HTMLIFrameElement} iframe The active selected iFrame.
     * 
     * @memberof TabStrip
     */
    switchTabUrl(mainWindow, id, iframe) {
        const newTabUrl = core.setQueryParameter(window.location.href, 'tab', id);

        // Using replaceState to fix history navigation to specific tab.
        window.history.replaceState({ path: newTabUrl }, null, newTabUrl);

        if (!this.isSwitchingTab) {
            // Using pushState as a tabstrip is nested iFrames in a main iFrame.
            // Because of the nesting there is no history entry created in the parent page.
             
            if (isEmbed) {
                const embedUrl = core.removeQueryParameter(newTabUrl, 'embed');
                 
                core.setEmbedUrl(embedUrl, true);
            }
            else {
                const newUrl = core.setQueryParameter(mainWindow.location.href, 'tab', id);
                mainWindow.history.pushState({ path: newTabUrl }, null, newUrl);
            }
        }
         
        topWindow.document.title = resources.consoleTitle.
            replace('{BrandName}', brandName).
            replace('{ClientBrandName}', clientBrandName).
            replace('{Page}', `: ${iframe.contentWindow.document.title}`);
    }
    // #endregion ********************************************************************************/

}

/**
 * Register web component.
 * Defines the custom element 'tab-strip' and maps it to the TabStrip class.
 */
customElements.define(TabStrip.tagName, TabStrip);
